import { ApiError } from '../api/client';
import { getProduktyPrehledStats, type ProduktyPrehledStats } from '../api/rondo';
import { useFeather } from '../shell/useFeather';
import { COUNTRY_NAME, TrendLineChart, dayFmt, dayShort, intFmt } from './ProduktyDetailShared';
import { PageState } from '../components/arc/page-state/page-state';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { Card } from '../components/arc/card/card';
import { Progress } from '../components/arc/progress/progress';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { useRondoPageData } from './useRondoPageData';
import './produkty-prehled.css';

export function ProduktyPrehledPage() {
  const { data: stats, error, loading } = useRondoPageData<ProduktyPrehledStats>('products-overview', getProduktyPrehledStats);

  useFeather([stats]);

  if (loading) return <PageState title="Načítám přehled produktů" loading />;
  if (error) return <PageState title="Přehled produktů se nepodařilo načíst" message={error instanceof ApiError ? error.message : 'Načtení selhalo'} />;
  if (!stats) return null;

  const todayLabel = stats.latestSnapshot ? dayShort(stats.latestSnapshot) : 'dnes';
  const strongestMarket = [...stats.byCountry].sort((a, b) => b.plays - a.plays)[0];
  const maxMarketPlays = Math.max(1, ...stats.byCountry.map((c) => c.plays));
  const dailyRows = stats.detail14d.map((day) => ({ ...day }));
  const dailyColumns: DataColumn<(typeof dailyRows)[number]>[] = [
    { key: 'date', label: 'Období', width: 150, render: (_, row) => dayFmt(row.date) },
    { key: 'cz', label: 'Česko', numeric: true, render: (_, row) => intFmt(row.cz) },
    { key: 'sk', label: 'Slovensko', numeric: true, render: (_, row) => intFmt(row.sk) },
    { key: 'ro', label: 'Rumunsko', numeric: true, render: (_, row) => intFmt(row.ro) },
    { key: 'total', label: 'Celkem', numeric: true, render: (_, row) => <strong>{intFmt(row.total)}</strong> },
  ];

  return (
    <section className="rondo-products-page rondo-products-overview rondo-arc-products-overview p1-arc-scope"><header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">PRODUKTY</div><h1>Přehled</h1><p>Výkon her a soutěží napříč trhy</p></div><span className="product-snapshot">Poslední data <strong>{stats.latestSnapshot ? dayFmt(stats.latestSnapshot) : '—'}</strong></span></header>

      <div className="rondo-arc-product-metrics">
        <MetricCard label="Dnes" value={stats.playsToday} context={`${todayLabel} · spinů`} />
        <MetricCard label="Posledních 7 dní" value={stats.plays7d} context="Spinů" />
        <MetricCard label="Posledních 30 dní" value={stats.plays30d} context="Spinů" />
        <MetricCard label="Unikátních hráčů" value={stats.avgUniqueUsersDay30d} context="Denní průměr za 30 dní" />
      </div>

      <div className="rondo-arc-product-insights">
        <Card title="Denní aktivita" description="CZ, SK a RO · posledních 30 dní" className="rondo-arc-product-chart-card">
          <div className="rondo-arc-product-chart">{stats.trend30d.length ? <TrendLineChart points={stats.trend30d}/> : <div className="product-empty">Žádná data.</div>}</div>
          <div className="rondo-arc-product-legend"><span className="cz"><i/>Česko</span><span className="sk"><i/>Slovensko</span><span className="ro"><i/>Rumunsko</span></div>
        </Card>
        <Card title="Výkon podle země" description={strongestMarket ? `Nejsilnější trh: ${COUNTRY_NAME[strongestMarket.country] ?? strongestMarket.country}` : 'Bez dat'} className="rondo-arc-product-markets-card">
          <div className="rondo-arc-product-markets">{stats.byCountry.map((c) => <div className="rondo-arc-product-market" key={c.country}>
            <div><span className="rondo-arc-product-market-code">{c.country}</span><div><strong>{COUNTRY_NAME[c.country] ?? c.country}</strong><small>{intFmt(c.uniqueUsers)} hráčů</small></div><strong>{intFmt(c.plays)} <small>spinů</small></strong></div>
            <Progress value={c.plays} max={maxMarketPlays} label={`${COUNTRY_NAME[c.country] ?? c.country}: ${intFmt(c.plays)} spinů`} />
          </div>)}</div>
        </Card>
      </div>

      <Card title="Denní vývoj podle trhu" description="Posledních 14 dní" className="rondo-arc-product-daily-card">
        <SortableDataTable rows={dailyRows} columns={dailyColumns} rowKey="date" caption="Denní aktivita produktů podle trhu" emptyMessage="Žádná data." />
      </Card>
    </section>
  );
}
